import type {
    ConfigTarget,
    ConfigValueSource,
    SettingsRegistryEntry,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import type {
    ExtensionManifest,
    ExtensionRuntimeType,
    ExtensionSourceInfo,
    JSONSchema,
} from "@magic-md/extension-sdk";

import { extensionUIRegistry } from "./extension-ui-registry";

export interface ExtensionSettingsModel {
    entries: SettingsRegistryEntry[];
    catalog: Record<string, {
        icon?: string;
        title?: string;
        description?: string;
        version?: string;
        category?: string;
        actionCount?: number;
        extensionRuntimeType?: ExtensionRuntimeType;
        extensionSource?: ExtensionSourceInfo;
    }>;
}

export function buildExtensionSettingsModel(): ExtensionSettingsModel {
    const entries: SettingsRegistryEntry[] = [];
    const catalog: ExtensionSettingsModel["catalog"] = {};

    for (const extension of extensionUIRegistry.getAll().values()) {
        const manifest = extension.manifest as ExtensionManifest;
        const title = manifest.title ?? manifest.name ?? manifest.id;
        const settingsDefaults = defaultsFromSettingsSchema(manifest.settingsSchema);

        catalog[manifest.id] = {
            icon: manifest.icon,
            title,
            description: manifest.description ?? manifest.overview,
            version: extension.source?.version ?? manifest.version,
            category: manifest.category,
            actionCount: countManifestCapabilities(manifest),
            extensionRuntimeType: manifest.runtime.type,
            extensionSource: extension.source,
        };

        entries.push({
            key: `extensions.${manifest.id}.enabled`,
            owner: "extension",
            scope: "machineOverridable",
            schema: {
                type: "boolean",
                default: true,
            },
            defaultValue: true,
            effect: "immediate",
            targets: ["user", "workspace", "remoteHost"],
            writableTargets: ["user", "workspace", "remoteHost"],
            title: `${title} enabled`,
            description: "Enable or disable this extension in the editor shell.",
        });

        if (manifest.settingsSchema || manifest.settingsPanel) {
            const settingsSchema = isRecord(manifest.settingsSchema)
                ? (manifest.settingsSchema as Record<string, unknown>)
                : { type: "object", additionalProperties: true };
            entries.push({
                key: `extensions.${manifest.id}.settings`,
                owner: "extension",
                scope: "machineOverridable",
                schema: settingsSchema,
                defaultValue: settingsDefaults,
                effect: "immediate",
                targets: ["user", "workspace", "remoteHost"],
                writableTargets: ["user", "workspace", "remoteHost"],
                title: `${title} settings`,
                description:
                    manifest.description ??
                    manifest.overview ??
                    "Extension-specific settings.",
            });
        }
    }

    return { entries, catalog };
}

export function buildExtensionSettingsSnapshot({
    entries,
    serviceSnapshot,
    settings,
    defaults,
    enabled,
}: {
    entries: SettingsRegistryEntry[];
    serviceSnapshot: SettingsSnapshot | null;
    settings: Record<string, Record<string, unknown>>;
    defaults: Record<string, Record<string, unknown>>;
    enabled: Record<string, boolean>;
}): SettingsSnapshot {
    const values: Record<string, unknown> = {};
    const meta: SettingsSnapshot["meta"]["entries"] = {};

    for (const entry of entries) {
        const parsed = parseExtensionSettingsKey(entry.key);
        if (!parsed) continue;
        const serviceMeta = serviceSnapshot?.meta.entries[entry.key];
        const targetValues: Partial<Record<ConfigTarget, unknown>> = {
            ...(serviceMeta?.targetValues ?? {}),
        };
        const setInTargets: Partial<Record<ConfigTarget, boolean>> = {
            ...(serviceMeta?.setInTargets ?? {}),
        };

        if (parsed.kind === "enabled") {
            if (Object.hasOwn(enabled, parsed.extensionId)) {
                targetValues.user = enabled[parsed.extensionId];
                setInTargets.user = true;
            }
            const source = resolveExtensionMetaSource(setInTargets);
            values[entry.key] = resolveLayeredBoolean(
                entry.defaultValue,
                targetValues.user,
                targetValues.workspace,
                targetValues.remoteHost,
            );
            meta[entry.key] = {
                source,
                effect: entry.effect,
                setInTargets,
                targetValues,
                readOnly: serviceMeta?.readOnly,
                readOnlyReason: serviceMeta?.readOnlyReason,
            };
            continue;
        }

        const explicitSettings = settings[parsed.extensionId];
        const defaultSettings = defaults[parsed.extensionId] ?? entry.defaultValue;
        if (explicitSettings) {
            targetValues.user = explicitSettings;
            setInTargets.user = true;
        }
        const source = resolveExtensionMetaSource(setInTargets);
        values[entry.key] = resolveLayeredObject(
            defaultSettings,
            targetValues.user,
            targetValues.workspace,
            targetValues.remoteHost,
        );
        meta[entry.key] = {
            source,
            effect: entry.effect,
            setInTargets,
            targetValues,
            readOnly: serviceMeta?.readOnly,
            readOnlyReason: serviceMeta?.readOnlyReason,
        };
    }

    const serviceCapabilities = serviceSnapshot?.capabilities;
    const availableTargets = dedupeTargets([
        ...(serviceCapabilities?.availableTargets ?? []),
        "user",
    ]);
    const serviceOwnerCapability = serviceCapabilities?.owners.extension;
    return {
        view: { values },
        meta: { entries: meta },
        diagnostics: serviceSnapshot?.diagnostics ?? [],
        capabilities: {
            availableTargets,
            owners: {
                extension: {
                    readableTargets: dedupeTargets([
                        ...(serviceOwnerCapability?.readableTargets ?? []),
                        "user",
                    ]),
                    writableTargets: dedupeTargets([
                        ...(serviceOwnerCapability?.writableTargets ?? []),
                        "user",
                    ]),
                },
            },
        },
        filePaths: serviceSnapshot?.filePaths,
        ownerFilePaths: serviceSnapshot?.ownerFilePaths,
    };
}

export function getEffectiveExtensionEnabled(
    snapshot: SettingsSnapshot,
    entries: SettingsRegistryEntry[],
): Record<string, boolean> {
    const enabled: Record<string, boolean> = {};
    for (const entry of entries) {
        const parsed = parseExtensionSettingsKey(entry.key);
        if (parsed?.kind !== "enabled") continue;
        enabled[parsed.extensionId] = snapshot.view.values[entry.key] !== false;
    }
    return enabled;
}

export function findAvailableTargetsForExtension(
    entries: SettingsRegistryEntry[],
    capabilities: SettingsSnapshot["capabilities"],
): ConfigTarget[] {
    return (["user", "workspace", "remoteHost"] satisfies ConfigTarget[]).filter(
        (target) =>
            entries.some((entry) => {
                if (entry.owner !== "extension") return false;
                if (!entry.targets.includes(target)) return false;
                if (!capabilities.availableTargets.includes(target)) return false;
                const ownerCapability = capabilities.owners.extension;
                return (
                    ownerCapability?.readableTargets.includes(target) ||
                    ownerCapability?.writableTargets.includes(target)
                );
            }),
    );
}

function resolveExtensionMetaSource(
    setInTargets: Partial<Record<ConfigTarget, boolean>>,
): ConfigValueSource {
    if (setInTargets.remoteHost) return "remoteHost";
    if (setInTargets.workspace) return "workspace";
    if (setInTargets.user) return "user";
    return "default";
}

function resolveLayeredBoolean(
    defaultValue: unknown,
    userValue: unknown,
    workspaceValue: unknown,
    remoteHostValue: unknown,
): boolean {
    let value = typeof defaultValue === "boolean" ? defaultValue : true;
    if (typeof userValue === "boolean") value = userValue;
    if (typeof workspaceValue === "boolean") value = workspaceValue;
    if (typeof remoteHostValue === "boolean") value = remoteHostValue;
    return value;
}

function resolveLayeredObject(
    defaultValue: unknown,
    userValue: unknown,
    workspaceValue: unknown,
    remoteHostValue: unknown,
): Record<string, unknown> {
    let value = isRecord(defaultValue) ? cloneRecord(defaultValue) : {};
    if (isRecord(userValue)) value = mergeRecords(value, userValue);
    if (isRecord(workspaceValue)) value = mergeRecords(value, workspaceValue);
    if (isRecord(remoteHostValue)) value = mergeRecords(value, remoteHostValue);
    return value;
}

function mergeRecords(
    base: Record<string, unknown>,
    next: Record<string, unknown>,
): Record<string, unknown> {
    const out = { ...base };
    for (const [key, value] of Object.entries(next)) {
        out[key] =
            isRecord(out[key]) && isRecord(value)
                ? mergeRecords(out[key] as Record<string, unknown>, value)
                : cloneUnknown(value);
    }
    return out;
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
    return cloneUnknown(value) as Record<string, unknown>;
}

function cloneUnknown<T>(value: T): T {
    if (value === undefined) return value;
    return JSON.parse(JSON.stringify(value)) as T;
}

function dedupeTargets(values: ConfigTarget[]): ConfigTarget[] {
    return [...new Set(values)];
}

function defaultsFromSettingsSchema(
    schema: JSONSchema | undefined,
): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (!schema) return out;
    if (isRecord(schema.default)) {
        Object.assign(out, schema.default);
    }
    if (!schema.properties) return out;
    for (const [key, prop] of Object.entries(schema.properties)) {
        if (isRecord(prop) && Object.hasOwn(prop, "default")) {
            out[key] = prop.default;
        }
    }
    return out;
}

export function parseExtensionSettingsKey(
    key: string,
): { extensionId: string; kind: "enabled" | "settings" } | null {
    const match = key.match(/^extensions\.([^.]+)\.(enabled|settings)$/);
    if (!match) return null;
    return {
        extensionId: match[1],
        kind: match[2] as "enabled" | "settings",
    };
}

function countManifestCapabilities(manifest: ExtensionManifest): number {
    const raw = manifest as Record<string, unknown>;
    const contributes = isRecord(raw.contributes) ? raw.contributes : {};
    const contract = isRecord(raw.contract) ? raw.contract : {};
    return [
        countArrayProperty(contributes, "commands"),
        countArrayProperty(contributes, "menus"),
        countArrayProperty(contributes, "sidebar"),
        countArrayProperty(contributes, "statusBar"),
        countArrayProperty(contributes, "unitActions"),
        countRecordProperty(contract, "methods"),
        countRecordProperty(contract, "events"),
    ].reduce((sum, count) => sum + count, 0);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function countArrayProperty(record: Record<string, unknown>, key: string): number {
    const value = record[key];
    return Array.isArray(value) ? value.length : 0;
}

function countRecordProperty(record: Record<string, unknown>, key: string): number {
    const value = record[key];
    return isRecord(value) ? Object.keys(value).length : 0;
}
